import React, { useState, useRef, useEffect } from "react";
import PlusIcon from "../../images/PlusIcon";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import CloseIcon from "../../images/CloseIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import {
  getAllJournalEntries,
  deleteJournalEntry,
  JournalEntry,
  JournalEntryStatus,
} from "../../services/journalEntries.service";
import { getLocations, getDetailLocations } from "../../services/locations.services";
import { useResponsive } from "../../hook/useResponsive";
import TagStatus from "../../components/TagStatus";
import { showApiError } from "../../services/apiError";
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  ListSortOption,
  fromSearchTime,
  toLocalDay,
} from "../../components/list";

interface InforTable {
  title: string;
  path: string;
  desc: string;
  titleCreate: string;
}

const isDraft = (status: string) => (status || "").toLowerCase() === "draft";

const formatDate = (dateString: string) => {
  const [y, m, d] = (dateString || "").split("-");
  if (!y || !m || !d) return "-";
  return `${d.padStart(2, "0")}.${m.padStart(2, "0")}.${y}`;
};

/** The kit's date filter holds dd.MM.yyyy; the API wants YYYY-MM-DD. */
const toApiDay = (value: string): string | undefined => {
  if (!value) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const day = fromSearchTime(value);
  return day ? toLocalDay(day) : undefined;
};

const JournalEntries = () => {
  const preLanguage = localStorage.getItem("language");
  const [language, setLanguage] = useState<any>(null);
  const { isTouchLayout: isMobile } = useResponsive();
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);

  const [rawRows, setRawRows] = useState<JournalEntry[]>([]);
  const [locationNameById, setLocationNameById] = useState<Record<string, string>>({});
  const locationIdsFetchedRef = useRef<Set<string>>(new Set());

  const inforTable: InforTable = {
    title:
      language && language?.["No journal entries yet"]
        ? language?.["No journal entries yet"]
        : "No journal entries yet",
    path: "/journal-entries/new",
    desc:
      language && language?.["Create manual journal entries to adjust your general ledger accounts."]
        ? language?.["Create manual journal entries to adjust your general ledger accounts."]
        : "Create manual journal entries to adjust your general ledger accounts.",
    titleCreate:
      language && language?.["New Journal Entry"]
        ? language?.["New Journal Entry"]
        : "New Journal Entry",
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      searchInputRef.current?.focus();
    }
  };

  useEffect(() => {
    if ((window as any).languageContext) {
      setLanguage((window as any).languageContext.language);
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const companyId = localStorage.getItem("companyId");
    if (!userId || !companyId) return;
    getLocations(userId, companyId, "1", "500", "")
      .then((res: any) => {
        const list = res?.data?.data ?? res?.data ?? [];
        const map: Record<string, string> = {};
        (Array.isArray(list) ? list : []).forEach((loc: any) => {
          if (loc?.id) {
            map[loc.id] = loc.locationName ?? loc.name ?? loc.location_name ?? loc.id;
          }
        });
        setLocationNameById(map);
      })
      .catch(() => {});
  }, []);

  // For each locationId in journal entries not yet in the map, fetch by ID (locations API for this locationId)
  useEffect(() => {
    const locationIds = [...new Set((rawRows || []).map((e: any) => e.locationId).filter(Boolean))] as string[];
    const toFetch = locationIds.filter(
      (id) => !locationNameById[id] && !locationIdsFetchedRef.current.has(id)
    );
    toFetch.forEach((locId: string) => {
      locationIdsFetchedRef.current.add(locId);
      getDetailLocations(locId)
        .then((res: any) => {
          const loc = res?.data?.data ?? res?.data ?? res;
          const name = loc?.locationName ?? loc?.name ?? loc?.location_name ?? locId;
          setLocationNameById((prev) => (prev[locId] === name ? prev : { ...prev, [locId]: name }));
        })
        .catch(() => {});
    });
  }, [rawRows, locationNameById]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft":
        return "#6A7878";
      case "Completed":
        return "#007C46";
      case "Cancelled":
        return "#BA0E13";
      default:
        return "#6A7878";
    }
  };

  const fetchJournalEntries = async (query: ListQuery) => {
    webLoadingFalse();
    const rawLocationId = localStorage.getItem("locationId") || "";
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const locationId = rawLocationId && uuidRegex.test(rawLocationId) ? rawLocationId : undefined;
    const res = await getAllJournalEntries(
      locationId,
      toApiDay(query.filters.fromDate),
      toApiDay(query.filters.toDate),
      (query.filters.status || undefined) as JournalEntryStatus | undefined,
      query.search || undefined,
      query.page,
      query.limit,
      query.sortBy || undefined,
      query.sortBy ? query.sortDir : undefined
    );
    const value = res?.data ?? {};
    const rows: JournalEntry[] =
      Array.isArray(value?.data) ? value.data
      : Array.isArray(value?.result?.data) ? value.result.data
      : Array.isArray(value?.result) ? value.result
      : Array.isArray(value) ? value
      : [];
    const total =
      typeof value?.total === "number" ? value.total
      : typeof value?.count === "number" ? value.count
      : typeof value?.result?.total === "number" ? value.result.total
      : rows.length;
    setRawRows(rows);
    return { rows, count: Number(total || 0) };
  };

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };

  const handleDeleteJournalEntry = async () => {
    try {
      await deleteJournalEntry(idDelete);
      listRef.current?.reload();
      setShowDelete(false);
    } catch (error) {
      console.error("Error deleting journal entry:", error);
      showApiError(error, { language });
      setShowDelete(false);
    }
  };

  const goToEntry = (id: string) => webNavigate("/" + preLanguage + "/journal-entries/" + id);

  const statusText = (status: string) => (status ? language?.[status] ?? status : "-");

  const columns: ListColumn<JournalEntry>[] = [
    {
      key: "internalNumber",
      label: "No.",
      width: 90,
      hideable: false,
      sortable: true,
      value: (e) => (typeof e?.internalNumber === "number" ? `#${e.internalNumber}` : "-"),
      mobile: "title",
      onClick: (e) => goToEntry(String(e.id)),
    },
    {
      key: "locationId",
      label: "Location",
      width: 180,
      value: (e) => (e?.locationId ? locationNameById[e.locationId] ?? e.locationId : "-"),
      mobile: "sheet",
    },
    {
      key: "comments",
      label: "Comment",
      width: 380,
      value: (e) => e?.comments || "",
      mobile: "hidden",
    },
    {
      key: "date",
      label: "Date",
      width: 130,
      sortable: true,
      value: (e) => formatDate(e?.date),
      mobile: "hidden",
    },
    {
      // Phone card line: the date first, then the comment.
      key: "dateComment",
      label: "Date",
      width: 0,
      desktop: false,
      value: (e) => [formatDate(e?.date), e?.comments].filter(Boolean).join(" · "),
      mobile: "subtitle",
    },
    {
      key: "status",
      label: "Status",
      width: 150,
      sortable: true,
      render: (e) => <TagStatus value={e?.status || ""} label={statusText(e?.status)} />,
      value: (e) => statusText(e?.status),
      mobile: "trailing",
    },
  ];

  const filters: ListFilter[] = [
    {
      key: "status",
      label: "Status",
      kind: "select",
      options: [
        { value: "draft", label: language?.["Draft"] ?? "Draft" },
        { value: "completed", label: language?.["Completed"] ?? "Completed" },
        { value: "cancelled", label: language?.["Cancelled"] ?? "Cancelled" },
      ],
    },
    { key: "fromDate", label: "From", kind: "date" },
    { key: "toDate", label: "To", kind: "date" },
  ];

  // Created is the API's own default order, so Ascending / Descending always applies.
  const sortOptions: ListSortOption[] = [
    { value: "createdAt", label: "Created" },
    { value: "internalNumber", label: "No." },
    { value: "date", label: "Date" },
    { value: "status", label: "Status" },
  ];

  const rowActions: ListRowAction<JournalEntry>[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "inventory",
      onRun: (e) => goToEntry(String(e.id)),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      hidden: (e) => !isDraft(e?.status),
      onRun: (e) => handleShowDelete(String(e.id)),
    },
  ];

  return (
    <>
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? "auto" : undefined,
          paddingBottom: isMobile ? "24px" : undefined,
          display: isMobile ? undefined : "flex",
          flexDirection: isMobile ? undefined : "column",
        }}
      >
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: "#222525" }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.["Journal Entries"]
              ? language?.["Journal Entries"]
              : "Journal Entries"}
          </p>
          <div
            className="flex items-center"
            style={{ gap: "16px", marginTop: isMobile ? "20px" : 0 }}
          >
            <div
              className="cursor-pointer flex items-center justify-center"
              style={{
                gap: "8px",
                padding: "8px 24px 8px 16px",
                backgroundColor: "#0F7679",
                borderRadius: "30px",
              }}
              onClick={() => {
                webNavigate("/" + preLanguage + "/journal-entries/new");
              }}
            >
              <div
                style={{ width: "24px", height: "24px" }}
                className="flex items-center justify-center"
              >
                <PlusIcon />
              </div>
              <p
                style={{ color: "#FFFFFF" }}
                className="text-[14px] leading-[130%] font-medium font-inter"
              >
                {language && language?.["New Journal Entry"]
                  ? language?.["New Journal Entry"]
                  : "New Journal Entry"}
              </p>
            </div>
          </div>
        </div>

        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchJournalEntries}
          searchPlaceholder="Search journal entries..."
          searchInputRef={searchInputRef}
          filters={filters}
          sortOptions={sortOptions}
          defaultSort={{ sortBy: "createdAt", sortDir: "DESC" }}
          rowActions={rowActions}
          countNoun="entries"
          emptyState={inforTable}
        />

        {/* Delete Modal - centered (same layout as confirm/warning modals) */}
        {showDelete && (
          <div
            className="fixed inset-0 flex items-center justify-center z-[200]"
            style={{ backgroundColor: "rgba(34, 37, 37, 0.32)" }}
            onClick={() => setShowDelete(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-lg font-inter mx-4"
              style={{ maxWidth: "440px", width: "100%", padding: "20px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-[20px] font-semibold leading-[24px]" style={{ color: "#222525" }}>
                  {language && language?.["Delete Journal Entry"]
                    ? language?.["Delete Journal Entry"]
                    : "Delete Journal Entry"}
                </p>
                <div
                  className="flex items-center justify-center cursor-pointer w-8 h-8 rounded-full hover:bg-[#EBF0F0]"
                  onClick={() => setShowDelete(false)}
                >
                  <CloseIcon />
                </div>
              </div>
              <p className="text-[14px] mb-6" style={{ color: "#4A5353" }}>
                {language && language?.["Are you sure you want to delete this journal entry? This action cannot be undone."]
                  ? language?.["Are you sure you want to delete this journal entry? This action cannot be undone."]
                  : "Are you sure you want to delete this journal entry? This action cannot be undone."}
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDelete(false)}
                  className="px-4 py-2 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
                  style={{ color: "#4A5353" }}
                >
                  {language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteJournalEntry}
                  className="px-4 py-2 rounded-full text-[14px] font-medium text-white"
                  style={{ backgroundColor: "#BA0E13" }}
                >
                  {language && language?.["Delete"] ? language?.["Delete"] : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

JournalEntries.displayName = "JournalEntries";
export default JournalEntries;
